import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import WhiteButtonIcon from '../utils/WhiteButtonIcon';
import WhiteButtonTrans from '../utils/WhiteButtonTrans';
import { ahorroEnCombo, mejoraComplementos } from '../utils/combos';

const CATEGORY_COLORS = {
  1: 'border-red-500',
  2: 'border-amber-500',
  3: 'border-blue-500',
  4: 'border-purple-500',
};

const ModalPacking = ({ isOpen, onClose, onConfirm, items, catalog = [] }) => {
  // Build initial packing state from cart items
  const initialPacking = useMemo(() => {
    if (!items || items.length === 0) return { charolas: [], sueltos: [] };

    // Only porción items are combo-eligible. Larger presentations are à la carte.
    const isPortion = (it) => !it.presentation || it.presentation === 'porcion';

    // 1. Expand items by quantity into individual instances
    const instances = [];
    items.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        instances.push({
          id: item.id,
          title: item.title,
          categoryId: item.categoryId,
          cartItemId: item.cartItemId,
          price: item.price,
          applyPromotions: item.applyPromotions,
          presentation: item.presentation,
          presentationLabel: item.presentationLabel,
          qty: item.qty,
          unit: item.unit,
          instanceIndex: i,
          // Unique key for this instance
          instanceKey: `${item.cartItemId}_${i}`,
        });
      }
    });

    // 2. Separate by category (only porción items can be a charola main/side).
    // Quién se queda con el combo cuando no alcanza para todos: lo que puede
    // armarlo y, de eso, lo que más ahorra en su combo (con un solo precio de
    // combo es lo más caro). Mismo criterio que el panel
    // (management/src/Utils/packing.js, porPrioridadDeCombo).
    const prioridadCombo = (a, b) =>
      (Number(b.applyPromotions) === 1) - (Number(a.applyPromotions) === 1)
      || ahorroEnCombo(b.id, b.price, catalog) - ahorroEnCombo(a.id, a.price, catalog);
    const principals = instances.filter(i => i.categoryId === 1 && isPortion(i)).sort(prioridadCombo);
    const complements = instances.filter(i => i.categoryId === 2 && isPortion(i)).sort(prioridadCombo);
    const others = instances.filter(i => !((i.categoryId === 1 || i.categoryId === 2) && isPortion(i)));

    // 3. Fill-first: se completa una charola antes de empezar la siguiente.
    // Repartir de a uno dejaba 3 platillos + 4 complementos en 2-1-1: un solo
    // combo donde caben dos. Se prefiere variedad dentro de cada charola.
    const charolas = principals.map(main => ({ main, sides: [] }));
    const remainingComplements = [...complements];
    const maxSides = 2;
    for (let i = 0; i < charolas.length; i++) {
      while (charolas[i].sides.length < maxSides && remainingComplements.length > 0) {
        const existingIds = charolas[i].sides.map(s => s.id);
        let bestIdx = remainingComplements.findIndex(c => !existingIds.includes(c.id));
        if (bestIdx === -1) bestIdx = 0;
        charolas[i].sides.push(remainingComplements[bestIdx]);
        remainingComplements.splice(bestIdx, 1);
      }
    }

    // Con combos que limitan complementos, intercambia hasta el reparto barato.
    mejoraComplementos(charolas, remainingComplements, catalog);

    // 4. Leftover complements go to sueltos
    const leftoverComplements = remainingComplements;

    // 5. Others are always sueltos
    const sueltos = [...leftoverComplements, ...others];

    return { charolas, sueltos };
  }, [items, catalog]);

  const [charolas, setCharolas] = useState(initialPacking.charolas);
  const [sueltos, setSueltos] = useState(initialPacking.sueltos);
  const [addingToCharola, setAddingToCharola] = useState(null); // index of charola being added to

  // Reset state when modal opens with new items
  React.useEffect(() => {
    if (isOpen) {
      setCharolas(initialPacking.charolas);
      setSueltos(initialPacking.sueltos);
      setAddingToCharola(null);
    }
  }, [isOpen, initialPacking]);

  // Available complements in sueltos (only cat 2 in porción)
  const availableComplements = sueltos.filter(
    s => s.categoryId === 2 && (!s.presentation || s.presentation === 'porcion')
  );

  const handleRemoveSide = (charolaIndex, sideIndex) => {
    const removed = charolas[charolaIndex].sides[sideIndex];
    setCharolas(prev => prev.map((ch, i) => {
      if (i !== charolaIndex) return ch;
      return { ...ch, sides: ch.sides.filter((_, si) => si !== sideIndex) };
    }));
    setSueltos(prev => [...prev, removed]);
  };

  const handleAddSide = (charolaIndex, instanceKey) => {
    const item = sueltos.find(s => s.instanceKey === instanceKey);
    if (!item) return;
    // Tope del combo: 1 platillo + hasta 2 complementos. El botón ya se
    // esconde al llegar a 2; esto evita que una charola de 3 se cuele por
    // otra vía (nunca arma combo y cocina la vería a precio suelto).
    if ((charolas[charolaIndex]?.sides.length || 0) >= 2) return;

    setSueltos(prev => prev.filter(s => s.instanceKey !== instanceKey));
    setCharolas(prev => prev.map((ch, i) => {
      if (i !== charolaIndex) return ch;
      return { ...ch, sides: [...ch.sides, item] };
    }));
    setAddingToCharola(null);
  };

  const handleConfirm = () => {
    const packingConfig = {
      charolas: charolas.map(ch => ({
        main: ch.main.id,
        main_name: ch.main.title,
        main_price: ch.main.price,
        main_applyPromotions: ch.main.applyPromotions,
        sides: ch.sides.map(s => s.id),
        sides_names: ch.sides.map(s => s.title),
        sides_prices: ch.sides.map(s => s.price),
        sides_applyPromotions: ch.sides.map(s => s.applyPromotions),
      })),
      sueltos: sueltos.map(s => ({
        id: s.id,
        name: s.title,
        categoryId: s.categoryId,
        price: s.price,
        // El backend re-arma el packing al guardar (_ligar_extras): sin este
        // dato no sabe que una bebida sin promociones no entra a combo.
        applyPromotions: s.applyPromotions,
        presentation: s.presentation,
        presentationLabel: s.presentationLabel,
        qty: s.qty,
        unit: s.unit,
      })),
    };
    onConfirm(packingConfig);
  };

  if (!isOpen) return null;

  const getCategoryBorder = (categoryId) =>
    CATEGORY_COLORS[categoryId] || 'border-gray-500';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="relative bg-gradient-to-br from-red-900/95 via-red-800/95 to-red-950/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden border border-white/20 max-h-[90vh] overflow-y-auto custom-scrollbar"
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.4
            }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-all duration-200 z-10 p-2 hover:bg-white/10 rounded-full"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="relative p-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Configurar Empaque</h2>
                <p className="text-white/70 text-sm">Cada charola tiene 1 platillo + hasta 2 complementos</p>
              </div>

              {/* Charolas */}
              <div className="space-y-4 mb-4">
                {charolas.map((charola, charolaIdx) => (
                  <div key={charolaIdx} className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-3">
                    <h4 className="text-white/80 text-xs font-semibold mb-2 uppercase tracking-wider">
                      Charola {charolaIdx + 1}
                    </h4>

                    {/* Main dish */}
                    <div className={`border-l-4 ${getCategoryBorder(1)} pl-2 mb-2`}>
                      <span className="text-white text-sm font-medium">{charola.main.title}</span>
                    </div>

                    {/* Sides */}
                    {charola.sides.map((side, sideIdx) => (
                      <div key={side.instanceKey} className={`border-l-4 ${getCategoryBorder(2)} pl-2 flex items-center justify-between gap-2 mb-1`}>
                        <span className="text-white/90 text-sm">{side.title}</span>
                        <button
                          onClick={() => handleRemoveSide(charolaIdx, sideIdx)}
                          className="text-red-300/80 hover:text-red-200 p-1 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                          title="Quitar de charola"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}

                    {/* Add complement button */}
                    {charola.sides.length < 2 && availableComplements.length > 0 && (
                      <>
                        {addingToCharola === charolaIdx ? (
                          <div className="mt-2 space-y-1">
                            <p className="text-white/60 text-xs mb-1">Selecciona un complemento:</p>
                            {availableComplements.map(comp => (
                              <button
                                key={comp.instanceKey}
                                onClick={() => handleAddSide(charolaIdx, comp.instanceKey)}
                                className={`w-full p-1.5 rounded-lg bg-white/5 hover:bg-white/15 transition-colors text-left border-l-4 ${getCategoryBorder(2)}`}
                              >
                                <span className="text-white/80 text-sm">{comp.title}</span>
                              </button>
                            ))}
                            <button
                              onClick={() => setAddingToCharola(null)}
                              className="text-white/50 text-xs hover:text-white/70 mt-1"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAddingToCharola(charolaIdx)}
                            className="mt-1 flex items-center gap-1.5 text-white/50 hover:text-white/80 text-sm transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Agregar complemento
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Sueltos */}
              {sueltos.length > 0 && (
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-3 mb-6">
                  <h4 className="text-white/60 text-xs font-semibold mb-2 uppercase tracking-wider">
                    Sueltos
                  </h4>
                  <div className="space-y-1">
                    {sueltos.map(item => (
                      <div key={item.instanceKey} className={`border-l-4 ${getCategoryBorder(item.categoryId)} pl-2`}>
                        <span className="text-white/70 text-sm">{item.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <WhiteButtonIcon
                  text="Confirmar Empaque"
                  onClick={handleConfirm}
                  className="justify-center font-bold text-sm rounded-xl transition-all duration-200 h-10 hover:scale-[1.02] shadow-lg"
                  iconPath="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
                <WhiteButtonTrans
                  text="Regresar"
                  onClick={onClose}
                  className="justify-center font-semibold text-sm rounded-xl transition-all duration-200 hover:bg-white/10 h-10"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModalPacking;
